import React, { useState } from "react";
import { Button, Input, Typography, Card, Alert, Radio, Space, Row, Col } from "antd";
import useApiCall from "../common/api-call";

const { TextArea } = Input;

const DebugByte = () => {
  const [planText, setPlanText] = useState("");
  const [memText, setMemText] = useState("");
  const [dataText, setDataText] = useState("");
  const [inputType, setInputType] = useState("plan"); // 'plan', 'mem', or 'data'
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { apiCall } = useApiCall();

  const handleDecode = async () => {
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      // Get the active text based on input type
      let textToDecode;
      if (inputType === "plan") {
        textToDecode = planText;
      } else if (inputType === "mem") {
        textToDecode = memText;
      } else {
        textToDecode = dataText;
      }

      if (!textToDecode.trim()) {
        setError("Please enter data to decode");
        setLoading(false);
        return;
      }

      const endpoint = "/debug/byte";
      const requestBody = {};

      // Set plan, mem, or data in the request body as string
      if (inputType === "plan") {
        requestBody.plan = textToDecode.trim();
      } else if (inputType === "mem") {
        requestBody.mem = textToDecode.trim();
      } else {
        requestBody.data = textToDecode.trim();
      }

      const response = await apiCall(endpoint, "POST", requestBody, "admin");
      setResult(response);
    } catch (error) {
      console.error("Error decoding:", error);
      setError(error.message || "Failed to decode data");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setPlanText("");
    setMemText("");
    setDataText("");
    setResult(null);
    setError(null);
  };

  const handleInputTypeChange = e => {
    setInputType(e.target.value);
    setResult(null);
    setError(null);
  };

  return (
    <div style={{ padding: 24 }}>
      <Card style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ marginBottom: 8 }}>
          Decode Plan/Memory/Data Bytes
        </Typography.Title>
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
          Enter base64 encoded string for plan, memory, or data to decode.
        </Typography.Text>

        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            Input Type
          </Typography.Text>
          <Radio.Group
            value={inputType}
            onChange={handleInputTypeChange}
            style={{ marginBottom: 16 }}
          >
            <Radio value="plan">Plan</Radio>
            <Radio value="mem">Memory</Radio>
            <Radio value="data">Data</Radio>
          </Radio.Group>
        </div>

        {error && (
          <Alert
            type="error"
            message={error}
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        {inputType === "plan" && (
          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
              Plan Bytes (Base64)
            </Typography.Text>
            <TextArea
              rows={8}
            value={planText}
            onChange={e => setPlanText(e.target.value)}
            placeholder="Enter plan bytes as base64 encoded string"
          />
          </div>
        )}

        {inputType === "mem" && (
          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
              Memory Bytes (Base64)
            </Typography.Text>
            <TextArea
              rows={8}
            value={memText}
            onChange={e => setMemText(e.target.value)}
            placeholder="Enter memory bytes as base64 encoded string"
          />
          </div>
        )}

        {inputType === "data" && (
          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
              Data Bytes (Base64)
            </Typography.Text>
            <TextArea
              rows={8}
            value={dataText}
            onChange={e => setDataText(e.target.value)}
            placeholder="Enter data bytes as base64 encoded string"
          />
          </div>
        )}

        <Space>
          <Button
            type="primary"
            onClick={handleDecode}
            disabled={
              loading ||
              (inputType === "plan"
                ? !planText.trim()
                : inputType === "mem"
                  ? !memText.trim()
                  : !dataText.trim())
            }
            loading={loading}
          >
            Decode
          </Button>
          <Button onClick={handleClear}>
            Clear
          </Button>
        </Space>
      </Card>

      {result && result.decoded && (
        <Card style={{ marginBottom: 24 }}>
          <Typography.Title level={5} style={{ marginBottom: 16 }}>
                Decoded Result
          </Typography.Title>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  overflow: "auto",
                  maxHeight: "600px",
                  backgroundColor: "#f5f5f5",
                  padding: "16px",
                  borderRadius: "4px",
                }}
              >
                {result.decoded}
              </pre>
          </Card>
      )}

      {result && !result.decoded && (
        <Card>
          <Typography.Title level={5} style={{ marginBottom: 16 }}>
            Response
          </Typography.Title>
          <pre style={{ overflow: "auto", maxHeight: "400px" }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Card>
      )}
    </div>
  );
};

export default DebugByte;

